import { Text } from '@app/components/core/typography/Text'
import { useTranslations } from '@i18n/useLanguage'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { CardWrapper } from '@app/components/blocks/card/CardWrapper'
import useMediaQuery from '@app/util/useMediaQuery'
import { Avatar } from '@app/components/core/avatar/Avatar'
import useDateFormat from '@app/util/useDateFormat'
import { IconButton } from '@app/components/core/button/IconButton'
import { faCheck, faEnvelope, faX } from '@fortawesome/pro-solid-svg-icons'
import { INITIATIVE_USER_ROLES } from '@schema/initiative'
import { useAuthState } from '@based/sdk'
import { match } from 'ts-pattern'
import { enrich } from '@i18n/localizeHelper'
import { SubMenu } from './SubMenu'
import { Action } from 'functions/app/dashboard/initiative/manage-participant'
import { DashboardInitiativeUser } from 'functions/app/dashboard/initiative/list-users'
import { fullName } from '@app/util/helpers'
import { pages } from '@app/util/links'
import { useLocation } from 'wouter'
import { TextLink } from '@app/components/core/link/TextLink'
const getJoinDate = (user: DashboardInitiativeUser): Date => {
  const date =
    user.$memberSince ??
    (user.$role === INITIATIVE_USER_ROLES.applicant
      ? user.$appliedAt
      : user.$followerSince) ??
    user.$adminSince

  return date ? new Date(date) : null
}

export type InitiativeUserProps = {
  user: DashboardInitiativeUser
  onAction: (action: Action | 'message') => Promise<void>
}

export const InitiativeUser = ({ user, onAction }: InitiativeUserProps) => {
  const authState = useAuthState()
  const dateFormat = useDateFormat(true)
  const isMobile = useMediaQuery()
  const translations = useTranslations().app.dashboard.initiative.people
  const [_, navigate] = useLocation()
  const { id, avatar, $role } = user
  const joinDate = getJoinDate(user)
  const name = fullName(user)

  const dateText = match($role)
    .with(INITIATIVE_USER_ROLES.applicant, () => translations.appliedOn)
    .with(INITIATIVE_USER_ROLES.follower, () => translations.followingSince)
    .otherwise(() => translations.addedOn)

  const dateSubtitle = enrich(dateText, dateFormat(joinDate))

  const isManageRowRendered =
    $role !== INITIATIVE_USER_ROLES.owner && id !== Number(authState.userId)

  const handleNavigateToProfile = () => {
    navigate(pages.profile(user.handle))
  }

  return (
    <CardWrapper
      padding={isMobile ? '1rem' : '1rem'}
      style={{
        border: 'var(--blue-border)',
      }}
    >
      <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Avatar
          size="small"
          variant="pink"
          image={avatar}
          labelSource={name}
          onClick={user.handle ? handleNavigateToProfile : undefined}
        />
        <Column gap={0} style={{ flex: '1 1 40rem', overflow: 'hidden' }}>
          {user.handle ? (
            <TextLink isBold to={pages.profile(user.handle)}>
              {name}
            </TextLink>
          ) : (
            <Text.Medium isBold>{name}</Text.Medium>
          )}

          <Text.ExtraSmall
            style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {dateSubtitle}
          </Text.ExtraSmall>
        </Column>
        {isManageRowRendered && (
          <Row style={{ flexGrow: 0 }}>
            <IconButton
              size="small"
              variant="secondary"
              icon={faEnvelope}
              onClick={() => void onAction('message')}
            />
            {match($role)
              .with(INITIATIVE_USER_ROLES.applicant, () => (
                <Row gap={'0.5rem'}>
                  <IconButton
                    variant="secondary"
                    size="small"
                    icon={faX}
                    onClick={() => void onAction('makeFollower')}
                  />
                  <IconButton
                    size="small"
                    icon={faCheck}
                    onClick={() => void onAction('makeMember')}
                  />
                </Row>
              ))
              .otherwise(() => (
                <SubMenu
                  role={$role}
                  onAction={(action) => void onAction(action)}
                />
              ))}
          </Row>
        )}
      </Row>
    </CardWrapper>
  )
}
